import { useCookie } from '#app';
import { nextTick, type Ref } from 'vue';

// 10 years in seconds - effectively "permanent"
const TEN_YEARS_IN_SECONDS = 60 * 60 * 24 * 365 * 10;

/**
 * Options for cookie storage
 */
export interface CookieStorageOptions<T> {
	/** Default value if cookie doesn't exist */
	default?: () => T;
	/** Max age in seconds. Default: 10 years (effectively permanent) */
	maxAge?: number;
	/** Cookie path. Default: '/' */
	path?: string;
	/** SameSite attribute. Default: 'lax' */
	sameSite?: 'lax' | 'strict' | 'none';
	/** Whether cookie is secure (HTTPS only). Default: auto-detect based on environment */
	secure?: boolean;
}

/**
 * Read a raw cookie value directly from document.cookie.
 * Nuxt's useCookie stores JSON-encoded, URI-encoded values.
 */
function readBrowserCookie<T>(name: string): T | undefined {
	if (typeof document === 'undefined') return undefined;
	const match = document.cookie.match(
		new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$1') + '=([^;]*)'),
	);
	if (!match) return undefined;
	try {
		return JSON.parse(decodeURIComponent(match[1]!));
	} catch {
		return decodeURIComponent(match[1]!) as T;
	}
}

/**
 * Composable for persisting reactive state in cookies
 * SSR-safe: works on both server and client
 *
 * This is a thin wrapper around Nuxt's useCookie with sensible defaults
 * for long-lived storage (10 year expiration by default).
 *
 * In static/prerendered builds, useCookie hydrates from the build-time
 * payload which contains defaults (no user cookies at build time). On the
 * client we read directly from document.cookie and sync the value into the
 * useCookie ref so all consumers see the correct persisted value.
 *
 * @param key The key to store the data under in the cookie
 * @param options Configuration options for the cookie
 * @returns A reactive ref that syncs with the cookie
 *
 * @example
 * ```ts
 * // Simple usage
 * const theme = useCookieStorage<'light' | 'dark'>('theme', {
 *   default: () => 'light'
 * });
 *
 * // Change the value (automatically persisted)
 * theme.value = 'dark';
 * ```
 */
export function useCookieStorage<T>(
	key: string,
	options: CookieStorageOptions<T> = {}
): Ref<T | null> {
	const {
		default: defaultValue,
		maxAge = TEN_YEARS_IN_SECONDS,
		path = '/',
		sameSite = 'lax',
		secure,
	} = options;

	// Leverage Nuxt's useCookie which is SSR-safe
	const cookie = useCookie<T | null>(key, {
		default: defaultValue ?? (() => null),
		maxAge,
		path,
		sameSite,
		// If secure is not specified, let Nuxt auto-detect based on environment
		...(secure !== undefined && { secure }),
	});

	// In static/prerendered builds, useCookie hydrates from the build-time
	// payload which has defaults. Read directly from document.cookie to get
	// the actual persisted value and sync it into the reactive ref.
	// We apply both synchronously AND after nextTick because Nuxt's hydration
	// may overwrite the synchronous assignment from the payload.
	if (import.meta.client) {
		const browserValue = readBrowserCookie<T>(key);
		if (browserValue !== undefined) {
			cookie.value = browserValue;
			nextTick(() => {
				// Use JSON.stringify for comparison — !== fails for objects
				// (readBrowserCookie creates a new object from JSON.parse).
				if (JSON.stringify(cookie.value) !== JSON.stringify(browserValue)) {
					cookie.value = browserValue;
				}
			});
		}
	}

	return cookie;
}
